/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable react/jsx-no-bind */
import React, { Fragment, SVGProps, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiMic, FiFile, FiFileText, FiImage, FiVideo } from "react-icons/fi";
import { WhatsAppMsg } from "services/api/AppWeb_WhatsApp/types";
import * as S from "../styles";
import { ImageLightbox, PdfViewerModal } from "./MediaViewer";

/* ── Check icons (estilo WhatsApp) ── */

function IconClock(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" {...props}>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 4.5V8.5L10.5 10"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSingleCheck(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" {...props}>
      <path
        d="M4 8.5L7 11.5L12.5 5.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDoubleCheck(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg width="16" height="14" viewBox="0 0 20 16" fill="none" {...props}>
      <path
        d="M1.8 8.6L4.8 11.6L10.2 6.2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.1 8.6L10.1 11.6L17.3 4.3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconError(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" {...props}>
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8 4.5V8.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11.2" r="0.8" fill="currentColor" />
    </svg>
  );
}

type VisualStatus = "pending" | "error" | "sent" | "delivered" | "read";

function mediaUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  // URLs do WhatsApp (mmg.whatsapp.net .enc) são criptografadas e podem
  // não carregar direto no browser — o onError dos elementos já trata isso.
  return url;
}

// Backend pode mandar mediaType como MIME ("image/jpeg"), label português
// ("imagem", "vídeo", "audio", "documento") ou inferimos pela extensão da URL.
function getMediaKind(
  msg: WhatsAppMsg
): "image" | "video" | "audio" | "pdf" | "doc" | null {
  if (!msg.mediaUrl && !msg.mediaType) return null;

  const type = (msg.mediaType || "").trim().toLowerCase();
  if (type.startsWith("image/") || type === "imagem" || type === "image")
    return "image";
  if (type.startsWith("video/") || type === "vídeo" || type === "video")
    return "video";
  if (type.startsWith("audio/") || type === "áudio" || type === "audio")
    return "audio";
  if (type === "application/pdf" || type === "pdf") return "pdf";

  // Fallback por extensão da URL.
  const path = (msg.mediaUrl || "").split("?")[0].toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp|heic|heif|avif)$/i.test(path)) return "image";
  if (/\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(path)) return "video";
  if (/\.(mp3|ogg|wav|m4a|aac|opus)$/i.test(path)) return "audio";
  if (path.endsWith(".pdf")) return "pdf";

  // URLs do nosso storage de WhatsApp não trazem extensão. Sem mais sinal,
  // tratamos como documento genérico.
  if (msg.mediaUrl) return "doc";
  return null;
}

// Extrai um nome amigável a partir da URL — última parte da path, decodificada.
function extractFileName(url: string | undefined): string {
  if (!url) return "Arquivo";
  try {
    const path = new URL(url).pathname;
    const last = path.split("/").filter(Boolean).pop() || "Arquivo";
    return decodeURIComponent(last);
  } catch {
    const last = url.split("/").filter(Boolean).pop() || "Arquivo";
    return last.split("?")[0];
  }
}

function getDocIcon(kind: ReturnType<typeof getMediaKind>): JSX.Element {
  if (kind === "pdf") return <FiFileText size={20} color="#fff" />;
  if (kind === "image") return <FiImage size={20} color="#fff" />;
  if (kind === "video") return <FiVideo size={20} color="#fff" />;
  return <FiFile size={20} color="#fff" />;
}

function getDocLabel(kind: ReturnType<typeof getMediaKind>): string {
  if (kind === "pdf") return "PDF";
  if (kind === "image") return "Imagem";
  if (kind === "video") return "Vídeo";
  if (kind === "audio") return "Áudio";
  return "Documento";
}

function formatMsgDate(iso: string): string {
  const d = new Date(iso);
  const agora = new Date();
  if (d.toDateString() === agora.toDateString()) return "Hoje";
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface MsgBubbleItemProps {
  msg: WhatsAppMsg;
  isLida?: boolean;
  onContextMenu?: (e: React.MouseEvent, msg: WhatsAppMsg) => void;
  isEditingTarget?: boolean;
  onOpenImage?: (src: string, fileName: string) => void;
  onOpenPdf?: (src: string, fileName: string) => void;
}

function MsgBubbleItem({
  msg,
  isLida,
  onContextMenu,
  isEditingTarget,
  onOpenImage,
  onOpenPdf,
}: MsgBubbleItemProps): JSX.Element {
  const saida = msg.direcao === "saida";
  const isPending = msg.id.startsWith("tmp-");
  const kind = getMediaKind(msg);
  const isImage = kind === "image";
  const isVideo = kind === "video";
  const isAudio = kind === "audio";
  const isPdf = kind === "pdf";
  const isDoc = !!msg.mediaUrl && !isImage && !isVideo && !isAudio;
  const fileName = extractFileName(msg.mediaUrl);

  const mediaLabel = (() => {
    if (isImage) return "📷 Imagem";
    if (isVideo) return "🎬 Vídeo";
    if (isAudio) return "🎵 Áudio";
    if (isPdf) return "📄 PDF";
    return "📎 Arquivo";
  })();

  const hora = new Date(msg.data).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const visualStatus: VisualStatus | undefined = (() => {
    if (!saida) return undefined;
    if (msg.status === "ERROR") return "error";
    if (msg.status === "PENDING" || (isPending && !msg.status))
      return "pending";
    if (msg.status === "READ") return "read";
    if (msg.status === "DELIVERY_ACK") return "delivered";
    if (msg.status === "SERVER_ACK") return "sent";
    if (isLida) return "read";
    return "delivered";
  })();

  return (
    <S.MsgRow saida={saida}>
      <S.MsgBubble
        saida={saida}
        pending={isPending}
        editingTarget={isEditingTarget}
        onContextMenu={onContextMenu ? (e) => onContextMenu(e, msg) : undefined}
      >
        {isImage && msg.mediaUrl && (
          <S.MsgMedia
            onClick={() => {
              const url = mediaUrl(msg.mediaUrl);
              if (url) onOpenImage?.(url, fileName);
            }}
          >
            <img
              src={mediaUrl(msg.mediaUrl)}
              alt={fileName}
              onError={(e) => {
                const el = e.target as HTMLImageElement;
                el.style.display = "none";
                const parent = el.closest("[data-media-wrapper]");
                if (parent) (parent as HTMLElement).style.display = "none";
              }}
            />
          </S.MsgMedia>
        )}

        {isVideo && msg.mediaUrl && (
          <S.MsgMedia>
            <video
              src={mediaUrl(msg.mediaUrl)}
              controls
              onError={(e) => {
                const el = e.target as HTMLVideoElement;
                el.style.display = "none";
                const parent = el.closest("[data-media-wrapper]");
                if (parent) (parent as HTMLElement).style.display = "none";
              }}
            />
          </S.MsgMedia>
        )}

        {isAudio && msg.mediaUrl && (
          <S.MsgAudio>
            <S.MsgAudioIcon saida={saida}>
              <FiMic size={16} />
            </S.MsgAudioIcon>
            <audio
              src={mediaUrl(msg.mediaUrl)}
              controls
              onError={(e) => {
                const el = e.target as HTMLAudioElement;
                el.style.display = "none";
              }}
            />
          </S.MsgAudio>
        )}

        {isDoc &&
          msg.mediaUrl &&
          (isPdf ? (
            <S.MsgDoc
              as="button"
              type="button"
              onClick={() => {
                const url = mediaUrl(msg.mediaUrl);
                if (url) onOpenPdf?.(url, fileName);
              }}
              style={{ cursor: "pointer", textAlign: "left" }}
            >
              <S.MsgDocIcon>{getDocIcon(kind)}</S.MsgDocIcon>
              <div style={{ minWidth: 0 }}>
                <S.MsgDocName>{fileName}</S.MsgDocName>
                <S.MsgDocHint>
                  {getDocLabel(kind)} · Clique para visualizar
                </S.MsgDocHint>
              </div>
            </S.MsgDoc>
          ) : (
            <S.MsgDoc
              href={mediaUrl(msg.mediaUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <S.MsgDocIcon>{getDocIcon(kind)}</S.MsgDocIcon>
              <div style={{ minWidth: 0 }}>
                <S.MsgDocName>{fileName}</S.MsgDocName>
                <S.MsgDocHint>
                  {getDocLabel(kind)} · Toque para abrir
                </S.MsgDocHint>
              </div>
            </S.MsgDoc>
          ))}

        <S.MsgText>
          {msg.mensagem && !isDoc && (
            <S.MsgContent>{msg.mensagem}</S.MsgContent>
          )}
          {!msg.mensagem && msg.mediaUrl && (
            <S.MsgContent style={{ fontStyle: "italic", opacity: 0.7 }}>
              {mediaLabel}
            </S.MsgContent>
          )}
          <S.MsgFooter>
            {msg.editado && (
              <S.MsgTime saida={saida} style={{ fontStyle: "italic" }}>
                editada
              </S.MsgTime>
            )}
            <S.MsgTime saida={saida}>{hora}</S.MsgTime>
            {saida && visualStatus && (
              <S.MsgStatus
                pending={visualStatus === "pending"}
                lida={visualStatus === "read"}
                error={visualStatus === "error"}
              >
                {visualStatus === "pending" && <IconClock />}
                {visualStatus === "error" && <IconError />}
                {visualStatus === "sent" && <IconSingleCheck />}
                {(visualStatus === "delivered" || visualStatus === "read") && (
                  <IconDoubleCheck />
                )}
              </S.MsgStatus>
            )}
          </S.MsgFooter>
        </S.MsgText>
      </S.MsgBubble>
    </S.MsgRow>
  );
}

interface MsgListProps {
  msgs: WhatsAppMsg[];
  onEditMessage?: (msg: WhatsAppMsg) => void;
  onDeleteMessage?: (msg: WhatsAppMsg) => void;
  editingMessageId?: string | null;
}

interface MsgContextMenuState {
  x: number;
  y: number;
  msg: WhatsAppMsg;
  canEdit: boolean;
  canDelete: boolean;
}

function isMediaMessage(msg: WhatsAppMsg): boolean {
  return Boolean(msg.mediaUrl || msg.mediaType);
}

function isLimitInFuture(iso: string | undefined): boolean {
  if (!iso) return true; // sem limite informado → permite (compatibilidade)
  const limit = new Date(iso).getTime();
  if (Number.isNaN(limit)) return true;
  return limit > Date.now();
}

interface MediaPreview {
  src: string;
  fileName: string;
}

export function MsgList({
  msgs,
  onEditMessage,
  onDeleteMessage,
  editingMessageId,
}: MsgListProps): JSX.Element {
  const lastEntradaIdx = msgs.reduce(
    (last, m, i) => (m.direcao === "entrada" ? i : last),
    -1
  );
  const [contextMenu, setContextMenu] = useState<MsgContextMenuState | null>(
    null
  );
  const [imagePreview, setImagePreview] = useState<MediaPreview | null>(null);
  const [pdfPreview, setPdfPreview] = useState<MediaPreview | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function handleOpenImage(src: string, fileName: string) {
    setImagePreview({ src, fileName });
  }

  function handleOpenPdf(src: string, fileName: string) {
    setPdfPreview({ src, fileName });
  }

  useEffect(() => {
    if (!contextMenu) return undefined;

    function closeMenu() {
      setContextMenu(null);
    }
    function handleMouseDown(e: MouseEvent) {
      if (menuRef.current?.contains(e.target as Node)) return;
      closeMenu();
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") closeMenu();
    }

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleEsc);
    document.addEventListener("scroll", closeMenu, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleEsc);
      document.removeEventListener("scroll", closeMenu, true);
    };
  }, [contextMenu]);

  function handleMsgContextMenu(e: React.MouseEvent, msg: WhatsAppMsg) {
    if (msg.direcao !== "saida") return;
    if (msg.id.startsWith("tmp-")) return;

    const canEdit =
      !isMediaMessage(msg) &&
      Boolean(msg.mensagem?.trim()) &&
      isLimitInFuture(msg.dataHoraLimiteEdicao);
    const canDelete = isLimitInFuture(msg.dataHoraLimiteExclusao);

    if (!canEdit && !canDelete) return;

    e.preventDefault();

    const menuWidth = 160;
    const visibleItems = (canEdit ? 1 : 0) + (canDelete ? 1 : 0);
    const menuHeight = visibleItems * 42;

    let x = e.clientX;
    let y = e.clientY;

    if (x + menuWidth > window.innerWidth - 8) {
      x = window.innerWidth - menuWidth - 8;
    }
    if (y + menuHeight > window.innerHeight - 8) {
      y = window.innerHeight - menuHeight - 8;
    }

    setContextMenu({
      x: Math.max(8, x),
      y: Math.max(8, y),
      msg,
      canEdit,
      canDelete,
    });
  }

  function handleEditClick() {
    if (!contextMenu || !contextMenu.canEdit || !onEditMessage) return;
    onEditMessage(contextMenu.msg);
    setContextMenu(null);
  }

  function handleDeleteClick() {
    if (!contextMenu || !onDeleteMessage) return;
    onDeleteMessage(contextMenu.msg);
    setContextMenu(null);
  }

  return (
    <>
      {msgs.map((m, i) => {
        const dateLabel = formatMsgDate(m.data);
        const prevLabel = i > 0 ? formatMsgDate(msgs[i - 1].data) : null;
        const isLida = m.direcao === "saida" && lastEntradaIdx > i;
        return (
          <Fragment key={m.id}>
            {dateLabel !== prevLabel && (
              <S.DateSeparator>
                <S.DateLabel>{dateLabel}</S.DateLabel>
              </S.DateSeparator>
            )}
            <MsgBubbleItem
              msg={m}
              isLida={isLida}
              onContextMenu={handleMsgContextMenu}
              isEditingTarget={editingMessageId === m.id}
              onOpenImage={handleOpenImage}
              onOpenPdf={handleOpenPdf}
            />
          </Fragment>
        );
      })}

      {contextMenu &&
        createPortal(
          <S.MsgContextMenu
            ref={menuRef}
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            {contextMenu.canEdit && onEditMessage && (
              <S.MsgContextMenuItem type="button" onClick={handleEditClick}>
                Editar mensagem
              </S.MsgContextMenuItem>
            )}
            {contextMenu.canDelete && onDeleteMessage && (
              <S.MsgContextMenuItem
                type="button"
                danger
                onClick={handleDeleteClick}
              >
                Excluir mensagem
              </S.MsgContextMenuItem>
            )}
          </S.MsgContextMenu>,
          document.body
        )}

      <ImageLightbox
        open={!!imagePreview}
        onClose={() => setImagePreview(null)}
        src={imagePreview?.src || ""}
        fileName={imagePreview?.fileName}
      />

      <PdfViewerModal
        open={!!pdfPreview}
        onClose={() => setPdfPreview(null)}
        src={pdfPreview?.src || ""}
        fileName={pdfPreview?.fileName}
      />
    </>
  );
}
